/**
 * Route guards for TanStack Router
 * Auth is verified via GET /auth/me (reads JWT from httpOnly cookie)
 */

import { redirect } from '@tanstack/react-router';
import { getMe } from '@/lib/api/auth';
import { queryClient } from '@/routes/__root';
import { authKeys } from '@/lib/query-keys';

/**
 * Check if user is authenticated via cookie-based JWT
 */
export async function checkAuth() {
  try {
    const result = await queryClient.ensureQueryData({
      queryKey: authKeys.user(),
      queryFn: () => getMe(),
      staleTime: 1000 * 60 * 60, // 1 hour
      gcTime: 1000 * 60 * 120, // 2 hours
    });

    if (!result.valid) {
      return null;
    }

    return result.user;
  } catch {
    return null;
  }
}

/**
 * Route guard: Requires authentication
 */
export async function requireAuth() {
  const user = await checkAuth();

  if (!user) {
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    });
  }

  return { user };
}

/**
 * Route guard: Requires guest (not authenticated)
 */
export async function requireGuest() {
  const user = await checkAuth();

  if (user) {
    throw redirect({
      to: '/',
    });
  }

  return { user: null };
}

/**
 * Route guard: Requires organizer role
 */
export async function requireOrganizer() {
  const user = await checkAuth();

  if (!user) {
    throw redirect({
      to: '/login',
      search: {
        redirect: window.location.pathname,
      },
    });
  }

  if (!user.isOrganizer) {
    throw redirect({
      to: '/',
    });
  }

  return { user };
}
